import React, { useEffect, useRef, useState } from "react";
import ReactPhoneInput from "react-phone-input-2";
import "react-phone-input-2/dist/style.css";
import PropTypes from "prop-types";
import { isEmpty } from "../../common/Common";
import ChevronSvg from "../../../assets/images/Chevron.svg";
import { browserAutofillGuardProps } from "./linchpin/LpInput";

export const detectCountryFromPhone = (phone) => {
    const cleanPhone = (phone || "").replace(/\D/g, "");
    if (!cleanPhone) return "us";
    if (cleanPhone.startsWith("91")) return "in";
    if (cleanPhone.startsWith("52")) return "mx";
    if (cleanPhone.startsWith("1")) return "us";
    return "us";
};

export const dialCodeByCountry = (countryCode) => {
    if (countryCode === "in") return "91";
    if (countryCode === "mx") return "52";
    return "1";
};

const maxPhoneLengthByCountry = (countryCode) => {
    if (countryCode === "in") return 12; // 91 + 10 digits
    if (countryCode === "mx") return 12; // 52 + 10 digits
    return 11; // 1 + 10 digits
};

export const dialDisplayByCountry = (countryCode) => {
    if (countryCode === "in") return "+91";
    if (countryCode === "mx") return "+52";
    return "+1";
};

// National masks applied in the input (dial code lives in the country selector).
const LINCHPIN_PHONE_MASKS = {
    us: '+. (...) ...-....',
    in: '+.. .....-.....',
    mx: '+.. .. ....-....',
};

const toDisplayDigits = (stored, countryCode, linchpin) => {
    const digits = (stored || "").replace(/\D/g, "");
    if (!linchpin || !digits) return digits;
    const dial = dialCodeByCountry(countryCode);
    return digits.startsWith(dial) ? digits.slice(dial.length) : digits;
};

const toStoredDigits = (displayDigits, countryCode, linchpin) => {
    const digits = (displayDigits || "").replace(/\D/g, "");
    if (!digits) return "";
    if (!linchpin) {
        return digits.slice(0, maxPhoneLengthByCountry(countryCode));
    }
    const dial = dialCodeByCountry(countryCode);
    const nationalMax = maxPhoneLengthByCountry(countryCode) - dial.length;
    const national = digits.startsWith(dial)
        ? digits.slice(dial.length, dial.length + nationalMax)
        : digits.slice(0, nationalMax);
    return national ? dial + national : "";
};

const MobileInput_v2 = (props) => {
    const linchpin = !!props.linchpin;
    const initialCountry = detectCountryFromPhone(props.value);
    const [prevCountryCode, setPrevCountryCode] = useState(initialCountry);
    const [phoneNumber, setPhoneNumber] = useState(() => {
        if (isEmpty(props.value)) return linchpin ? "" : "1";
        if (linchpin) return toDisplayDigits(props.value, initialCountry, true);
        return (props.value || "").replace(/\+/g, "");
    });

    // Sync from parent only when props.value actually changes — avoids wiping
    // in-progress keystrokes when other deps (e.g. prevCountryCode) tick.
    const lastExternalValue = useRef(props.value);
    const prevDigitLenRef = useRef(0);
    useEffect(() => {
        if (props.value === lastExternalValue.current) return;
        lastExternalValue.current = props.value;

        if (isEmpty(props.value)) {
            setPhoneNumber(linchpin ? "" : dialCodeByCountry(prevCountryCode));
            prevDigitLenRef.current = 0;
            return;
        }
        const country = detectCountryFromPhone(props.value);
        setPrevCountryCode(country);
        const display = linchpin
            ? toDisplayDigits(props.value, country, true)
            : (props.value || "").replace(/\+/g, "");
        setPhoneNumber(display);
        prevDigitLenRef.current = display.replace(/\D/g, "").length;
    }, [props.value, linchpin]);

    const nationalMaxLen = (countryCode) =>
        maxPhoneLengthByCountry(countryCode) - dialCodeByCountry(countryCode).length;

    const isExplicitCountryPick = (value, reportedCountry) => {
        if (!reportedCountry || reportedCountry === prevCountryCode) return false;
        const digits = (value || "").replace(/\D/g, "");
        const dial = dialCodeByCountry(reportedCountry);
        // A country dropdown pick never ADDS a national digit: react-phone-input-2
        // re-emits the same national number, an empty value, or just the new dial
        // code. Typing that makes the library auto-guess a different country instead
        // INCREASES the digit count. So any country change that didn't grow the
        // number is an explicit pick — preserve the current number, just re-dial it.
        // (Country change never grows the national number; only typing does.)
        return digits === dial || digits.length <= prevDigitLenRef.current;
    };

    const applyCountryChange = (reportedCountry, nationalDigits, data) => {
        const displayValue = nationalDigits.slice(0, nationalMaxLen(reportedCountry));
        const storedValue = toStoredDigits(displayValue, reportedCountry, true);
        setPrevCountryCode(reportedCountry);
        // react-phone-input-2 empties its own internal number when a country is picked
        // from the dropdown. Re-setting phoneNumber to the SAME national digits won't
        // re-sync it (value prop unchanged), so the field goes blank. Clear then restore
        // on the next tick so the library's value-change effect fires under the new
        // country and re-displays the preserved digits.
        setPhoneNumber("");
        setTimeout(() => setPhoneNumber(displayValue), 0);
        prevDigitLenRef.current = displayValue.length;
        lastExternalValue.current = storedValue;
        props.handleInputChange(storedValue, data);
    };

    const handleInputChange = (value, countryCode, data) => {
        const sanitizedDigits = (value || "").replace(/\D/g, "");
        const country = countryCode || prevCountryCode;
        if (linchpin) {
            const displayValue = sanitizedDigits.slice(0, nationalMaxLen(country));
            const storedValue = toStoredDigits(displayValue, country, true);
            setPhoneNumber(displayValue);
            prevDigitLenRef.current = displayValue.length;
            lastExternalValue.current = storedValue;
            props.handleInputChange(storedValue, data);
            return;
        }
        const clampedValue = sanitizedDigits.slice(0, maxPhoneLengthByCountry(country));
        setPhoneNumber(clampedValue);
        lastExternalValue.current = clampedValue;
        props.handleInputChange(clampedValue, data);
    };

    const handleChange = (value, data) => {
        const reportedCountry = data?.countryCode;

        if (linchpin) {
            if (isExplicitCountryPick(value, reportedCountry)) {
                const nationalDigits = phoneNumber.replace(/\D/g, "");
                applyCountryChange(reportedCountry, nationalDigits, data);
                return;
            }
            // Keep the user-selected country while typing — react-phone-input-2
            // auto-guesses country from digits and would otherwise flip +91 → +1.
            handleInputChange(value, prevCountryCode, data);
            return;
        }

        if (reportedCountry && reportedCountry !== prevCountryCode) {
            setPrevCountryCode(reportedCountry);
            setPhoneNumber(data.dialCode || "");
            lastExternalValue.current = "";
            props.handleInputChange("", data);
            return;
        }
        handleInputChange(value, reportedCountry, data);
    };

    const handleKeyDown = (e) => {
        if (e.keyCode === 32) {
            e.preventDefault();
        }
    };

    const phoneInputRef = useRef(null);
    const wrapperRef = useRef(null);

    // react-phone-input-2 re-focuses this field on every value/format update
    // (its internal cursorToEnd → input.focus()). When the field sits below an
    // open control — e.g. the Mode A Service Level dropdown above the legs —
    // that unsolicited focus yanks focus away and closes the dropdown (and
    // leaves the field perpetually blue-ringed). The library ignores our ref
    // (it owns numberInputRef), so grab the actual input from the wrapper and
    // guard focus(): only honor it when it won't steal focus from another field
    // the user is actively in; genuine clicks/typing on THIS field still work.
    useEffect(() => {
        const el = wrapperRef.current && wrapperRef.current.querySelector("input.form-control");
        if (!el || el.__focusGuarded) return undefined;
        const nativeFocus = el.focus.bind(el);
        el.__focusGuarded = true;
        el.focus = function (...a) {
            const ae = document.activeElement;
            const stealingFromAnother =
                ae && ae !== el && ae !== document.body &&
                (/^(INPUT|SELECT|TEXTAREA)$/.test(ae.tagName) ||
                    ae.isContentEditable ||
                    (ae.closest && ae.closest('.ant-select, [role="combobox"], [role="listbox"]')));
            if (stealingFromAnother) return undefined;
            return nativeFocus.apply(this, a);
        };
        return () => {
            try { el.focus = nativeFocus; delete el.__focusGuarded; } catch (e) { /* noop */ }
        };
    }, []);

    const wrapperCls = ["updatedDateStyle", props.wrapperClassName]
        .filter(Boolean)
        .join(" ");
    const dialDisplay = dialDisplayByCountry(prevCountryCode);

    const autofillInputProps = {
        ...(props.disableBrowserAutofill
            ? {
                  ...browserAutofillGuardProps({
                      disabled: props.disabled,
                      unlockRef: phoneInputRef,
                  }),
                  name: props.name,
                  ref: phoneInputRef,
              }
            : { ref: phoneInputRef }),
        ...(props["data-testid"] ? { "data-testid": props["data-testid"] } : {}),
    };

    const linchpinStyle = linchpin
        ? {
              "--lp-phone-dial": `"${dialDisplay}"`,
              "--lp-phone-chevron": `url(${ChevronSvg})`,
          }
        : undefined;

    return (
        <div
            ref={wrapperRef}
            className={wrapperCls}
            style={linchpinStyle}
            data-country={linchpin ? prevCountryCode : undefined}
        >
            <ReactPhoneInput
                defaultCountry={prevCountryCode}
                onlyCountries={["us", "in", "mx"]}
                dropdownStyle={{ width: "220px" }}
                inputClass="ant-input"
                buttonClass={linchpin ? "lav2-phone__selector" : ""}
                dropdownClass={linchpin ? "lav2-phone__dropdown" : ""}
                disableAreaCodes
                value={phoneNumber}
                placeholder={props.placeholder}
                inputStyle={
                    linchpin
                        ? { width: "100%", height: "100%", border: "none", boxShadow: "none" }
                        : props.inputStyle || { width: "100%", height: "32px" }
                }
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                inputProps={autofillInputProps}
                autoFormat
                masks={linchpin ? LINCHPIN_PHONE_MASKS : undefined}
                disabled={props.disabled}
                enableLongNumbers={false}
                // disableCountryCode strips "+1" from the field; countryCodeEditable
                // must be true in that mode or react-phone-input-2 rejects every
                // keystroke whose length is less than "+{dialCode}".
                countryCodeEditable={linchpin}
                disableCountryCode={linchpin}
            />
        </div>
    );
};

export default MobileInput_v2;

MobileInput_v2.propTypes = {
    value: PropTypes.string,
    handleInputChange: PropTypes.func.isRequired,
};

MobileInput_v2.defaultProps = {
    value: "",
};